import type { InjectionKey } from 'vue';
import type {
  ComponentDeviceAvailability,
  ComponentDeviceKind,
  ComponentDevicePosition,
} from 'shared';

export interface ComponentDeviceAdapter {
  ensureSafeRuntime?: () => Promise<void>;
  capture?: () => Promise<Blob | null>;
  locate?: () => Promise<ComponentDevicePosition>;
  onBackground?: (handler: () => void) => () => void;
}
export const componentDeviceAdapterKey: InjectionKey<ComponentDeviceAdapter> = Symbol(
  'component-device-adapter',
);

export function deviceError(code: string, message: string) {
  return Object.assign(new Error(message), { code, status: 0, retryable: false });
}

export function deviceFailure(error: unknown) {
  if (error instanceof Error && 'code' in error && String(error.code).startsWith('DEVICE_'))
    return error;
  const name = error && typeof error === 'object' && 'name' in error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError')
    return deviceError(
      'DEVICE_PERMISSION_DENIED',
      '设备权限被拒绝，请在系统或浏览器设置中允许访问',
    );
  if (name === 'NotFoundError' || name === 'OverconstrainedError')
    return deviceError('DEVICE_UNAVAILABLE', '当前设备没有可用的相机或麦克风');
  if (name === 'NotReadableError')
    return deviceError('DEVICE_BUSY', '设备被其他应用占用，请关闭后重试');
  return deviceError('DEVICE_OPERATION_FAILED', '设备操作失败，请检查系统权限及设备状态后重试');
}

export function deviceAvailability(adapter: ComponentDeviceAdapter): ComponentDeviceAvailability {
  const media = Boolean(window.isSecureContext && navigator.mediaDevices?.getUserMedia);
  return {
    camera: Boolean(adapter.capture || media),
    location: Boolean(adapter.locate || (window.isSecureContext && navigator.geolocation)),
    microphone: media && typeof MediaRecorder !== 'undefined',
    motion: Boolean(window.isSecureContext && typeof DeviceMotionEvent !== 'undefined'),
    orientation: Boolean(window.isSecureContext && typeof DeviceOrientationEvent !== 'undefined'),
  };
}

export function getDevicePosition(): Promise<ComponentDevicePosition> {
  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(copyDevicePosition(position)),
      (error) =>
        reject(
          deviceError(
            error.code === 1
              ? 'DEVICE_PERMISSION_DENIED'
              : error.code === 3
                ? 'DEVICE_TIMEOUT'
                : 'DEVICE_UNAVAILABLE',
            error.code === 1
              ? '位置权限被拒绝，请在设置中允许访问'
              : error.code === 3
                ? '获取位置超时，请重试'
                : '无法获取位置，请检查系统定位服务',
          ),
        ),
      { timeout: 10_000, maximumAge: 0, enableHighAccuracy: false },
    ),
  );
}

export function copyDevicePosition(position: {
  coords: Pick<ComponentDevicePosition['coords'], 'latitude' | 'longitude' | 'accuracy'> &
    Partial<ComponentDevicePosition['coords']>;
  timestamp: number;
}): ComponentDevicePosition {
  const { latitude, longitude, accuracy, altitude, altitudeAccuracy, heading, speed } =
    position.coords;
  return {
    coords: {
      latitude,
      longitude,
      accuracy,
      altitude: altitude ?? null,
      altitudeAccuracy: altitudeAccuracy ?? null,
      heading: heading ?? null,
      speed: speed ?? null,
    },
    timestamp: position.timestamp,
  };
}

export async function deviceJpeg(input: Blob): Promise<File> {
  if (!input.size || !/^image\/(jpeg|png|webp|heic|heif)$/i.test(input.type))
    throw deviceError('DEVICE_INVALID_IMAGE', '请选择有效的照片文件');
  const url = URL.createObjectURL(input);
  const image = new Image();
  try {
    image.src = url;
    await image.decode();
    if (!image.naturalWidth || !image.naturalHeight) throw new Error('无效图片尺寸');
    const canvas = document.createElement('canvas');
    const scale = Math.min(1, 4096 / Math.max(image.naturalWidth, image.naturalHeight));
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('无法创建图片画布');
    // 浏览器解码校正 EXIF 方向，重新编码去掉 GPS 等元数据。
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const jpeg = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('无法编码图片'))),
        'image/jpeg',
        0.9,
      ),
    );
    return new File([jpeg], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
  } catch {
    throw deviceError('DEVICE_INVALID_IMAGE', '照片无法解码，请选择 JPEG、PNG 或 WebP 图片');
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function requestDeviceSensor(
  kind: Extract<ComponentDeviceKind, 'motion' | 'orientation'>,
) {
  const constructor = (kind === 'motion'
    ? DeviceMotionEvent
    : DeviceOrientationEvent) as unknown as {
    requestPermission?: () => Promise<string>;
  };
  if (constructor.requestPermission && (await constructor.requestPermission()) !== 'granted')
    throw deviceError('DEVICE_PERMISSION_DENIED', '运动与方向权限被拒绝，请在设置中允许访问');
}

export async function captureDeviceVideo(video: HTMLVideoElement): Promise<File> {
  if (!video.videoWidth || !video.videoHeight)
    throw deviceError('DEVICE_UNAVAILABLE', '相机画面尚未就绪，请稍后重试');
  const canvas = document.createElement('canvas');
  const scale = Math.min(1, 4096 / Math.max(video.videoWidth, video.videoHeight));
  canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
  canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
  const context = canvas.getContext('2d');
  if (!context) throw deviceError('DEVICE_OPERATION_FAILED', '无法创建照片画布');
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (image) =>
        image ? resolve(image) : reject(deviceError('DEVICE_OPERATION_FAILED', '照片编码失败')),
      'image/jpeg',
      0.9,
    ),
  );
  return new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
}

export function recordDeviceAudio(
  stream: MediaStream,
  ready: (file: File) => void,
  failed: (error: unknown) => void,
) {
  const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'].find((type) =>
    MediaRecorder.isTypeSupported(type),
  );
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks: Blob[] = [];
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout>;
  const release = () => {
    clearTimeout(timer);
    stream.getTracks().forEach((track) => track.stop());
  };
  const stop = () => {
    if (recorder.state !== 'inactive') recorder.stop();
    release();
  };
  recorder.ondataavailable = (event) => {
    if (!cancelled && event.data.size) chunks.push(event.data);
  };
  recorder.onerror = () => {
    cancelled = true;
    stop();
    failed(deviceError('DEVICE_RECORDING_FAILED', '录音失败，请重新操作'));
  };
  recorder.onstop = () => {
    release();
    if (cancelled) return;
    const type = recorder.mimeType || chunks[0]?.type;
    const blob = new Blob(chunks, { type });
    if (!blob.size) {
      failed(deviceError('DEVICE_RECORDING_FAILED', '录音为空，请重新录音'));
      return;
    }
    const extension = type?.includes('mp4') ? 'm4a' : type?.includes('ogg') ? 'ogg' : 'webm';
    ready(new File([blob], `recording-${Date.now()}.${extension}`, { type }));
  };
  recorder.start(1000);
  timer = setTimeout(stop, 60_000);
  return {
    stop,
    cancel: () => {
      cancelled = true;
      stop();
    },
  };
}
